import { gql, useMutation } from '@apollo/client';
import Button from '@orn/shared-web/components/Button/Button';
import Form from '@orn/shared-web/components/Form';
import Input from '@orn/shared-web/components/Input';
import Modal from '@orn/shared-web/components/Modal';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import type {
  UpdateRecurringTransactionMutation,
  UpdateRecurringTransactionMutationVariables,
} from './__generated__/UpdateRecurringTransactionModal.gql';

const UPDATE_RECURRING_TRANSACTION = gql`
  mutation UpdateRecurringTransaction($input: UpdateRecurringTransactionInput!) {
    updateRecurringTransaction(input: $input) {
      id
      description
    }
  }
`;

const MAX_NAME_LENGTH = 255;

interface IRecurringStream {
  id: string;
  description: string;
}

interface IBaseUpdateRecurringTransactionModalProps {
  recurringTransaction: IRecurringStream;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSubmit: (data: { description: string }) => void;
  isLoading?: boolean;
}

/**
 * Presentational rename form for a recurring stream — a single name input
 * prefilled with the current display name, validated (non-empty, max 255
 * characters) before submit.
 */
export const BaseUpdateRecurringTransactionModal = ({
  recurringTransaction,
  isOpen,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: IBaseUpdateRecurringTransactionModalProps) => {
  const [description, setDescription] = React.useState(recurringTransaction.description);

  React.useEffect(() => {
    setDescription(recurringTransaction.description);
  }, [recurringTransaction.id, recurringTransaction.description]);

  const { isValid, errorMessage } = React.useMemo(() => {
    const trimmedDescription = description.trim();
    if (trimmedDescription.length === 0) {
      return {
        isValid: false,
        errorMessage: 'Name cannot be empty',
      };
    }
    if (trimmedDescription.length > MAX_NAME_LENGTH) {
      return {
        isValid: false,
        errorMessage: 'Name cannot be longer than 255 characters',
      };
    }
    return {
      isValid: true,
      errorMessage: '',
    };
  }, [description]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      onSubmit({ description: description.trim() });
    }
  };

  // Unify every dismiss path (Cancel, header X, Escape, overlay click) through one
  // handler so an unsaved draft is reset whenever the modal closes — react-aria
  // routes Escape/overlay dismissal through Modal's onOpenChange, not the buttons.
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setDescription(recurringTransaction.description);
    }
    onOpenChange(open);
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={handleOpenChange} className="w-100">
      <Modal.Header onClose={() => handleOpenChange(false)}>
        <div className="flex flex-col gap-0.5">
          <div className="text-primary text-2xl font-semibold">Recurring stream details</div>
          <div className="text-tertiary text-sm font-normal">
            Renaming this stream changes how it appears across the app. The name detected from your bank is kept.
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="flex-col items-start gap-5 px-6 pb-6 pt-0">
        <Form onSubmit={handleSubmit} className="w-full">
          <div className="flex w-full flex-col gap-8">
            <div className="flex w-full">
              <Input
                label="Name"
                value={description}
                onChange={setDescription}
                isInvalid={!isValid}
                hint={errorMessage}
              />
            </div>
            <div className="flex w-full gap-3">
              <Button
                color="secondary"
                onClick={() => handleOpenChange(false)}
                isDisabled={isLoading}
                className="flex-1"
                size="lg"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                isLoading={isLoading}
                isDisabled={!isValid || isLoading}
                className="flex-1"
                size="lg"
              >
                Save details
              </Button>
            </div>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

interface IUpdateRecurringTransactionModalProps {
  recurringTransaction: IRecurringStream;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSubmit?: (data: { description: string }) => void;
}

/**
 * Data-connected rename modal: persists the new display name via
 * `updateRecurringTransaction` and closes on success. Apollo's normalized
 * cache propagates the renamed `description` to every query selecting this
 * stream, so every surface naming it updates without refetching.
 */
const UpdateRecurringTransactionModal = ({
  recurringTransaction,
  isOpen,
  onOpenChange,
  onSubmit,
}: IUpdateRecurringTransactionModalProps) => {
  const [updateRecurringTransaction, { loading }] = useMutation<
    UpdateRecurringTransactionMutation,
    UpdateRecurringTransactionMutationVariables
  >(UPDATE_RECURRING_TRANSACTION);

  const handleSubmit = async (data: { description: string }) => {
    try {
      await updateRecurringTransaction({
        variables: {
          input: {
            id: recurringTransaction.id,
            description: data.description,
          },
        },
      });

      onSubmit?.(data);
      onOpenChange(false);
    } catch (error) {
      logger.error('Failed to update recurring transaction:', error);
    }
  };

  return (
    <BaseUpdateRecurringTransactionModal
      recurringTransaction={recurringTransaction}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit}
      isLoading={loading}
    />
  );
};

export default UpdateRecurringTransactionModal;
